import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  createMatterIncidentReportV3,
  deleteMatterIncidentReportV1,
  updateMatterIncidentReportV1,
} from "@/lib/data/requests/matters/matter-incident-report";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type MatterV3 } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

export const useCreateMatterIncidentReport = ({
  showSuccessToast = true,
}: {
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterIncidentReportV3>[0]) =>
      createMatterIncidentReportV3(data),
    onMutate: async (data) => {
      const queryKey = [queryKeys.matters, data.matterId];
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<APIResponse<MatterV3>>(queryKey);

      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              incidentReports: [
                ...oldData.data.incidentReports,
                {
                  ...data,
                  connections: [],
                  dateCreated: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error(
        variables.noReport
          ? "Error marking no incident report"
          : variables.notApplicable
            ? "Error marking incident report as not applicable"
            : "Error creating incident report",
      );
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: (_data, variables) => {
      if (showSuccessToast) {
        toast.success(
          variables.noReport
            ? "Incident report marked as no report"
            : variables.notApplicable
              ? "Incident report marked as not applicable"
              : "Incident report created successfully",
        );
      }
    },
  });
};

export const useUpdateMatterIncidentReport = ({
  showSuccessToast = true,
}: {
  showSuccessToast?: boolean;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterIncidentReportV1>[0]) =>
      updateMatterIncidentReportV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              incidentReports: oldData.data.incidentReports.map(
                (incidentReport) =>
                  incidentReport.id === data.id
                    ? { ...incidentReport, ...data }
                    : incidentReport,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData<APIResponse<MatterV3>>(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating incident report");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Incident report updated successfully");
      }
    },
  });
};

export const useDeleteMatterIncidentReport = ({
  successMessage = "Incident report deleted successfully",
  errorMessage = "Error deleting incident report",
}: { successMessage?: string; errorMessage?: string } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterIncidentReportV1>[0]) =>
      deleteMatterIncidentReportV1(data),
    onMutate: async (data) => {
      const queryKey = [queryKeys.matters, data.matterId];
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<APIResponse<MatterV3>>(queryKey);
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              incidentReports: oldData.data.incidentReports.filter(
                (incidentReport) => incidentReport.id !== data.id,
              ),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, errorMessage);
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success(successMessage);
    },
  });
};
